import type { Config } from '@gmn-portal/backend';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDebounce } from 'use-debounce';
import type { Where } from '@/core/api';
import { payloadClient } from '@/core/api';
import { useMediaBlobUrl } from '@/core/api/media';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui';
import { buttonVariants } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils';

export type ComboBoxItemType = {
  value: string;
  label: string;
  imageId?: string | null;
};

type SearchComboboxProps<T extends keyof Config['collections']> = {
  value?: string;
  onSelect: (value: string | undefined | null) => void;
  className?: string;
  unselect?: boolean;
  unselectMsg?: string;
  collectionKey: T;
  searchKey: keyof Config['collections'][T];
  getItemLabel?: (entity: Config['collections'][T]) => string;
  imageKey?: keyof Config['collections'][T];
  sortBy?: string;
  excludeIds?: string[];
  error?: string;
  autoFocus?: boolean;
  texts?: {
    selectItemMsg?: string;
    searchPlaceholder?: string;
    noResultsMsg?: string;
    searchingMsg?: string;
    errorMsg?: string;
    loadMoreMsg?: string;
  };
  icon?: LucideIcon;
  xs?: boolean;
  customWhere?: Where;
  clearValue?: null | undefined | '';
  disabled?: boolean;
};

const popOverStyles = {
  width: 'var(--radix-popover-trigger-width)',
};

const ITEMS_PER_PAGE = 10;

export function SearchCombobox<T extends keyof Config['collections']>({
  value,
  onSelect,
  className,
  unselect = false,
  collectionKey,
  searchKey,
  getItemLabel,
  imageKey,
  sortBy,
  excludeIds = [],
  error,
  autoFocus = false,
  texts,
  icon: Icon,
  xs = false,
  customWhere,
  clearValue = null,
  disabled = false,
}: SearchComboboxProps<T>) {
  const { t } = useIntl();

  texts = {
    selectItemMsg: t('searchCombobox.selectItemMsg'),
    searchPlaceholder: t('searchCombobox.searchPlaceholder'),
    noResultsMsg: t('searchCombobox.noResultsMsg'),
    searchingMsg: t('searchCombobox.searchingMsg'),
    errorMsg: t('errors.generalError'),
    loadMoreMsg: t('common.loadMore'),
    ...texts,
  };

  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

  const { ref: loadMoreRef, inView } = useInView();

  const { data: selectedItem } = useInfiniteQuery({
    queryKey: [collectionKey, 'byId', value],
    queryFn: async () => {
      if (!value) return null;
      const item = await payloadClient.findById({
        collection: collectionKey,
        id: value,
      });
      return {
        value: item.id,
        label: getItemLabel
          ? getItemLabel(item as Config['collections'][T])
          : (item[searchKey] as string),
        imageId: imageKey ? (item[imageKey] as string) : undefined,
      } as ComboBoxItemType;
    },
    enabled: !!value,
    getNextPageParam: () => undefined,
    initialPageParam: 1,
  });

  const {
    data: searchResults,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      collectionKey,
      searchKey,
      debouncedSearchQuery,
      excludeIds,
      customWhere,
      imageKey,
      sortBy,
    ],
    queryFn: async ({ pageParam }) => {
      const baseWhere: Where = {
        and: [
          ...(debouncedSearchQuery
            ? [
                {
                  [searchKey]: {
                    like: debouncedSearchQuery,
                  },
                },
              ]
            : []),
          {
            id: {
              not_in: excludeIds,
            },
          },
        ],
      };

      const finalWhere = customWhere ? { and: [baseWhere, customWhere] } : baseWhere;

      const response = await payloadClient.find({
        collection: collectionKey,
        where: finalWhere,
        limit: ITEMS_PER_PAGE,
        page: pageParam,
        ...(sortBy ? { sort: sortBy as unknown as never } : {}),
      });
      return {
        items: response.docs.map((entity) => {
          const id = String((entity as Config['collections'][T]).id);
          const typedEntity = entity as Config['collections'][T];
          const label = String(
            getItemLabel
              ? getItemLabel(typedEntity)
              : (typedEntity[searchKey as keyof Config['collections'][T]] as unknown as string)
          );
          const imageId = imageKey
            ? ((entity as Config['collections'][T])[
                imageKey as keyof Config['collections'][T]
              ] as unknown as string | undefined)
            : undefined;
          return { value: id, label, imageId } as ComboBoxItemType;
        }),
        nextPage: response.hasNextPage ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: open,
    initialPageParam: 1,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSelectResult = (selectedValue: string) => {
    onSelect(selectedValue);
    setOpen(false);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && !disabled) {
      setOpen(true);
      inputRef.current?.focus();
    }
  }, [autoFocus, disabled]);

  const handleClear = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    onSelect(clearValue);
    setOpen(false);
  };

  const flattenedResults = searchResults?.pages.flatMap((page) => page.items) || [];

  return (
    <Popover open={open && !disabled} onOpenChange={(isOpen) => !disabled && setOpen(isOpen)}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={open}
          aria-haspopup="dialog"
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'justify-between flex px-3 w-full group text-muted-foreground hover:text-foreground',
            className,
            error && 'border-red-500',
            xs && 'h-8',
            disabled && 'pointer-events-none opacity-50'
          )}
        >
          {selectedItem ? (
            <>
              <div className="flex items-center gap-2 min-w-0">
                <SelectedItemContent
                  item={selectedItem.pages[0] || undefined}
                  icon={Icon}
                  xs={xs}
                  showImage={!!imageKey}
                />
              </div>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear(e);
                }}
                className="text-muted-foreground hover:text-foreground shrink-0"
              >
                <X className={cn('ml-2 shrink-0', xs ? 'size-3.5' : 'size-4')} />
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 min-w-0">
                {Icon && (
                  <Icon
                    className={cn('shrink-0 text-muted-foreground', xs ? 'size-3.5' : 'size-4')}
                  />
                )}
                <span className="truncate">{texts?.selectItemMsg}</span>
              </div>

              <ChevronsUpDown
                className={cn('ml-2 shrink-0 text-muted-foreground', xs ? 'size-3.5' : 'size-4')}
              />
            </>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent style={popOverStyles} className="p-0">
        <Command shouldFilter={false}>
          <CommandInput
            ref={inputRef}
            placeholder={texts?.searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <div className="max-h-[300px] overflow-auto">
            <CommandList className="max-h-none">
              {isLoading ? (
                <CommandEmpty>{texts?.searchingMsg}</CommandEmpty>
              ) : isError ? (
                <CommandEmpty>{texts?.errorMsg}</CommandEmpty>
              ) : (
                <CommandEmpty>{texts?.noResultsMsg}</CommandEmpty>
              )}
              <CommandGroup>
                {unselect && (
                  <CommandItem value="" onSelect={() => handleSelectResult('')}>
                    <Check
                      className={cn('mr-2 h-4 w-4', value === '' ? 'opacity-100' : 'opacity-0')}
                    />
                    {t('searchCombobox.unselectMsg')}
                  </CommandItem>
                )}
                {flattenedResults.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.value}
                    onSelect={() => handleSelectResult(item.value)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        value === item.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <ComboboxItemContent item={item} showImage={!!imageKey} />
                  </CommandItem>
                ))}
                {hasNextPage && (
                  <CommandItem ref={loadMoreRef} disabled>
                    {isFetchingNextPage ? t('common.loading') : t('common.loadMore')}
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SelectedItemContent({
  item,
  icon: Icon,
  xs,
  showImage,
}: {
  item?: ComboBoxItemType;
  icon?: LucideIcon;
  xs: boolean;
  showImage: boolean;
}) {
  const { data: blobUrl } = useMediaBlobUrl(showImage && item?.imageId ? item.imageId : null);

  if (!item) return null;

  return (
    <>
      {showImage && item.imageId && blobUrl ? (
        <img
          src={blobUrl}
          alt=""
          className={cn('rounded object-cover shrink-0', xs ? 'size-5' : 'size-6')}
        />
      ) : Icon ? (
        <Icon className={cn('shrink-0 text-muted-foreground', xs ? 'size-3.5' : 'size-4')} />
      ) : null}
      <span className="text-foreground truncate" title={item.label}>
        {item.label}
      </span>
    </>
  );
}

function ComboboxItemContent({ item, showImage }: { item: ComboBoxItemType; showImage: boolean }) {
  const { data: blobUrl } = useMediaBlobUrl(showImage && item.imageId ? item.imageId : null);

  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      {showImage && item.imageId && blobUrl && (
        <img src={blobUrl} alt="" className="size-6 rounded object-cover shrink-0" />
      )}
      <span className="min-w-0 truncate">{item.label}</span>
    </div>
  );
}
